// Verifies fetched `.luis` source text against explicit browser policy pins.
// Cross-origin sources fail closed unless a matching SRI hash is configured.
import { readRuntimeSecurityPolicy } from './url-policy'
import { isSafeRuntimeNetworkUrl, isSameOriginRuntimeUrl, resolveRuntimeSecurityUrl } from './url-shared'

const SRI_ALGORITHMS: Record<string, AlgorithmIdentifier> = {
  sha256: 'SHA-256',
  sha384: 'SHA-384',
  sha512: 'SHA-512',
}

// Verifies source integrity when one `.luis` URL crosses the current origin.
export async function verifyLuisSourceIntegrity(rawUrl: string, baseHref: string, source: string): Promise<boolean> {
  const resolved = resolveRuntimeSecurityUrl(rawUrl, baseHref)
  // Reject invalid or unsafe source URLs before integrity lookup.
  if (!resolved || !isSafeRuntimeNetworkUrl(resolved)) {
    return false
  } else if (isSameOriginRuntimeUrl(resolved)) {
    // Same-origin sources keep the default trusted-site path.
    return true
  } else {
    // Continue cross-origin source integrity enforcement.
  }

  const expected = readRuntimeSecurityPolicy().luisSourceIntegrity?.[resolved.href]?.trim()
  // Cross-origin `.luis` sources require an exact absolute-URL pin.
  if (!expected) {
    return false
  } else {
    // Compare source content against one or more configured SRI tokens.
    return verifySriText(source, expected)
  }
}

// Compare one text payload against a space-separated SRI token list.
async function verifySriText(source: string, expected: string): Promise<boolean> {
  // Fail closed when browser hashing APIs are unavailable.
  if (typeof crypto === 'undefined' || !crypto.subtle || typeof TextEncoder === 'undefined') {
    return false
  } else {
    // Continue hashing with the available Web Crypto implementation.
  }

  // Matches whitespace between SRI tokens. Example match: " "
  const tokens = expected.split(/\s+/).filter(Boolean)
  // Check every token because SRI strings may include multiple algorithms.
  for (const token of tokens) {
    if (await verifySriToken(source, token)) {
      return true
    } else {
      // Continue checking remaining integrity tokens.
    }
  }

  // Return false when no token matched the fetched source.
  return false
}

// Verify one `sha*-base64` SRI token against text content.
async function verifySriToken(source: string, token: string): Promise<boolean> {
  const separator = token.indexOf('-')
  // Reject malformed integrity tokens.
  if (separator <= 0) {
    return false
  } else {
    // Continue token algorithm and digest extraction.
  }

  const algorithmName = token.slice(0, separator).toLowerCase()
  const expectedDigest = token.slice(separator + 1)
  const algorithm = SRI_ALGORITHMS[algorithmName]
  // Reject unsupported algorithms and empty digest bodies.
  if (!algorithm || !expectedDigest) {
    return false
  } else {
    // Continue digest calculation for supported SRI algorithms.
  }

  try {
    // Hash the fetched `.luis` text exactly as UTF-8 bytes.
    const digest = await crypto.subtle.digest(algorithm, new TextEncoder().encode(source))
    // Return final constant-shape digest comparison result.
    return arrayBufferToBase64(digest) === expectedDigest
  } catch {
    // Treat unexpected hashing failures as integrity failures.
    return false
  }
}

// Convert a Web Crypto digest buffer to the base64 form used by SRI.
function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer)
  let binary = ''
  // Build a binary string in small chunks to avoid call-stack limits.
  for (let index = 0; index < bytes.length; index += 8192) {
    const chunk = bytes.subarray(index, index + 8192)
    binary += String.fromCharCode(...chunk)
  }

  // Return browser-native base64 output.
  return btoa(binary)
}
